import React from "react";
import { Link } from "react-router-dom";
import { Panel, PanelBody } from "../../components/panel/panel.jsx";

import { useComprobantes } from "../../hooks_cache/useComprobantes.js";

import { format, parseISO } from "date-fns";
import { formatCurrency } from "../../utils/utils_number.js";

import Chart from "chart.js/auto";
import { Bar } from "react-chartjs-2";

const fechaMinima = new Date();
fechaMinima.setMonth(fechaMinima.getMonth() - 12);
fechaMinima.setDate(1);
const fechaMinimaStr = fechaMinima.toISOString().split("T")[0];

const Home = () => {
  const { data: comprobantes } = useComprobantes();
  const comprobantesPendientes =
    comprobantes?.filter((c) => c.estado === "pendiente" && c.TipoDTE.codigo !== "05" && c.fecEmi >= fechaMinimaStr) ||
    [];

  // Agrupar por mes y acumular totales
  const resumenMensual = {};

  comprobantesPendientes.forEach((comp) => {
    const fecha = parseISO(comp.fecEmi);
    const mesClave = format(fecha, "MM/yyyy"); // Ej: "2024-03"

    if (!resumenMensual[mesClave]) {
      resumenMensual[mesClave] = {
        mes: mesClave,
        totalGravada: 0,
        totalNoSuj: 0,
        totalExenta: 0,
        totalPagar: 0,
      };
    }

    resumenMensual[mesClave].totalGravada += comp.totalGravada || 0;
    resumenMensual[mesClave].totalNoSuj += comp.totalNoSuj || 0;
    resumenMensual[mesClave].totalExenta += comp.totalExenta || 0;
    resumenMensual[mesClave].totalPagar += comp.totalPagar || 0;
  });

  const resumenMensualArray = Object.values(resumenMensual).sort(
    (a, b) => new Date(`01/${a.mes}`) - new Date(`01/${b.mes}`)
  );

  // Construir datos para el gráfico
  const labels = resumenMensualArray.map((item) => item.mes);
  const dataGravada = resumenMensualArray.map((item) => item.totalGravada);
  const dataExenta = resumenMensualArray.map((item) => item.totalExenta);
  const dataNoSuj = resumenMensualArray.map((item) => item.totalNoSuj);

  const data = {
    labels,
    datasets: [
      {
        label: "Ventas Sujetas",
        data: dataGravada,
        backgroundColor: "rgba(144, 238, 144, 0.6)",
        borderColor: "rgba(60, 179, 113, 1)",
        borderWidth: 1,
      },
      {
        label: "Ventas Exenta",
        data: dataExenta,
        backgroundColor: "rgba(173, 216, 230, 0.6)",
        borderColor: "rgba(100, 149, 237, 1)",
        borderWidth: 1,
      },
      {
        label: "Ventas No Sujeta",
        data: dataNoSuj,
        backgroundColor: "rgba(255, 182, 193, 0.6)",
        borderColor: "rgba(255, 105, 180, 1)",
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          afterBody: (items) => {
            const index = items[0].dataIndex;
            const ventaTotal = (dataGravada[index] || 0) + (dataExenta[index] || 0) + (dataNoSuj[index] || 0);
            const iva = (dataGravada[index] || 0) * 0.13;
            const pagoCuenta = (dataGravada[index] || 0) * 0.0175;

            return [
              `💵 Venta general: $${ventaTotal.toFixed(2)}`,
              `🧾 IVA (13%): $${iva.toFixed(2)}`,
              `💰 Pago a Cuenta (1.75%): $${pagoCuenta.toFixed(2)}`,
            ];
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`,
        },
      },
    },
  };

  const hoy = new Date();

  const resumen = comprobantesPendientes.reduce(
    (acc, c) => {
      const pendiente = c.pendiente || 0;
      const vencimiento = new Date(c.fecha_vencimiento);

      acc.pendientes.cantidad += 1;
      acc.pendientes.monto += pendiente;

      if (vencimiento < hoy) {
        acc.vencidos.cantidad += 1;
        acc.vencidos.monto += pendiente;
      } else {
        acc.aVencer.cantidad += 1;
        acc.aVencer.monto += pendiente;
      }

      return acc;
    },
    {
      pendientes: { cantidad: 0, monto: 0 },
      vencidos: { cantidad: 0, monto: 0 },
      aVencer: { cantidad: 0, monto: 0 },
    }
  );

  // console.log(resumen);

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/">Home</Link>
        </li>
        <li className="breadcrumb-item active">Home</li>
      </ol>
      <h1 className="page-header">ANALISIS DE VENTAS</h1>

      <div className="row ">
        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-teal">
            <div className="stats-icon stats-icon-lg">
              <i className="fa fa-dollar-sign fa-fw"></i>
            </div>
            <div className="stats-content">
              <div className="stats-title">COMPROBANTES POR COBRAR</div>
              <div className="stats-number">{formatCurrency(resumen.aVencer.monto)}</div>
            </div>
            <div className="stats-desc">{resumen.aVencer.cantidad} comprobantes por vencer</div>
          </div>
        </div>
        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-red">
            <div className="stats-icon stats-icon-lg">
              <i className="fa fa-warning fa-fw"></i>
            </div>
            <div className="stats-content">
              <div className="stats-title">COMPROBANTES POR COBRAR VENCIDOS</div>
              <div className="stats-number">{formatCurrency(resumen.vencidos.monto)}</div>
            </div>
            <div className="stats-desc">{resumen.vencidos.cantidad} comprobantes vencidos</div>
          </div>
        </div>
        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-dark">
            <div className="stats-icon stats-icon-lg">
              <i className="fa fa-dollar-sign fa-fw"></i>
            </div>
            <div className="stats-content">
              <div className="stats-title">COMPROBANTES PENDIENTES POR COBRAR</div>
              <div className="stats-number">{formatCurrency(resumen.pendientes.monto)}</div>
            </div>
            <div className="stats-desc">{resumen.pendientes.cantidad} comprobantes pendientes de cobrar</div>
          </div>
        </div>
      </div>

      <Panel>
        <PanelBody>
          <div className="row">
            <div className="col-md-12" style={{ height: "270px", width: "100%" }}>
              <Bar data={data} options={options} />;
            </div>
          </div>
        </PanelBody>
      </Panel>

      <div className="col-12 col-xl-7 col-md-7">
        <div className="mb-10px mt-10px fs-10px">
          <b className="text-dark">TOP 10 - COMPROBANTES VENCIDOS MÁS ANTIGUOS</b>
        </div>
      </div>
    </div>
  );
};

export default Home;
